import React from "react";
import { Helmet } from "react-helmet-async";

const SITE_NAME = "TRADECRAFT";
const DEFAULT_DESC =
  "TRADECRAFT is AI-native market intelligence and a trader-development platform. Learn how professionals reason about markets with deterministic evidence and transparent AI. Educational analysis only — not financial advice.";
const DEFAULT_IMAGE = "/social/default.jpg";
// Actual dimensions of the generated share images in /public/social.
const IMG_W = 1264;
const IMG_H = 848;

function siteBase() {
  if (typeof window !== "undefined" && window.location) return window.location.origin;
  return process.env.PUBLIC_SITE_URL || process.env.REACT_APP_PUBLIC_SITE_URL || process.env.REACT_APP_BACKEND_URL || "";
}

/**
 * Per-page SEO/AEO/GEO head management for the PUBLIC web layer.
 * Authenticated/app + auth-utility pages should pass noindex (default here is noindex,
 * matching the base template) so only explicitly-public pages are indexable.
 */
export default function Seo({
  title,
  description = DEFAULT_DESC,
  path = "/",
  image = DEFAULT_IMAGE,
  imageAlt = "TRADECRAFT — AI-native market intelligence",
  noindex = true,
  type = "website",
  jsonLd = null,
  noCanonical = false,
}) {
  const base = siteBase();
  const url = base ? `${base}${path}` : path;
  const fullTitle = !title ? `${SITE_NAME} — AI-Native Market Intelligence & Trading Education`
    : title.endsWith(`| ${SITE_NAME}`) ? title : `${title} — ${SITE_NAME}`;
  const img = image && image.startsWith("http") ? image : `${base}${image}`;
  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {!noCanonical && <link rel="canonical" href={url} />}
      <meta name="robots" content={noindex ? "noindex, nofollow" : "index, follow"} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      {!noCanonical && <meta property="og:url" content={url} />}
      <meta property="og:image" content={img} />
      <meta property="og:image:width" content={String(IMG_W)} />
      <meta property="og:image:height" content={String(IMG_H)} />
      <meta property="og:image:alt" content={imageAlt} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={img} />
      <meta name="twitter:image:alt" content={imageAlt} />
      {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
    </Helmet>
  );
}
